import Vue from "vue";
import Vuex from "vuex";
import { apiGet, apiPost } from '@/services/api';

import assets from './modules/assets';
import buckets from './modules/buckets'
import positions from './modules/positions';
import user from './modules/user.js';

import { UNDERWATER_BUY_RATIO } from '@/constants/trading';
import { PRICING_MODE, SYNC_COOLDOWN_MS, SYNC_REFRESH_INTERVAL_MS } from '@/constants/sync';
import { SYNC_STATUS } from '@/constants/status';

Vue.use(Vuex);

export default new Vuex.Store({
    modules: {
        assets,
        buckets,
        positions,
        user,
    },
    state: {
        baseURL: {
            production: 'https://warren-api.ciops.ro',
            development: 'http://localhost:5050',
        },
        env: process.env.NODE_ENV,

        syncStatus: SYNC_STATUS.NOT_SYNCED,
        syncTook: 'N/A',
        processingTook: 'N/A',
        dbUsed: null,
        pricingTime: null,
        // Every price each source answered with, from the last sync. Never persisted.
        assetPrices: {},
        requestWarnings: [],

        intervalCheckAPI: null, // timer for refreshing data
        canSync: true,

        userStats: {},
        userData: {},
        pricingMode: PRICING_MODE.DELAYED,

        events: [],
        
        // PWA update management
        updateAvailable: false,
        updateRegistration: null,
    },
    getters: {
        /* The sync's prices for one symbol, the source in use first. The API decides
           what each tag means; this only reads `in-use` to order and flag the rows. */
        getPricesForSymbol: (state) => (symbol) => {
            const entry = state.assetPrices[symbol];
            if (!entry) return [];

            return [...(entry.sources || [])]
                .map(row => ({
                    ...row,
                    tags: row.tags || [],
                    stored: (row.tags || []).includes('in-use'),
                }))
                .sort((left, right) => Number(right.stored) - Number(left.stored));
        },
        getEnv(state) {
            return state.env;
        },
        assetsToBuy(state) {
            const underwater = [];
            const allAssets = state.assets.assetList;

            for (let i = 0; i < allAssets.length; i++) {
                if (!allAssets[i].cached_stats?.lowest_open_position) continue;
                if (allAssets[i].conviction === 'snoozed') continue;

                const lowestPosition = allAssets[i].cached_stats.lowest_open_position;
                if (allAssets[i].price >= lowestPosition.open_price) continue;

                const ratio = allAssets[i].price / lowestPosition.open_price;
                const conviction = allAssets[i].acts_like_conviction;
                // how far underwater counts as a buy depends on conviction
                if (!((ratio <= UNDERWATER_BUY_RATIO.high && conviction === 'high')
                    || (ratio <= UNDERWATER_BUY_RATIO.null && conviction === null)
                    || (ratio <= UNDERWATER_BUY_RATIO.low && conviction === 'low'))) continue;

                underwater.push({
                    symbol: allAssets[i].symbol,
                    indicator: (ratio - 1) * 100,
                    asset: allAssets[i],
                    lowestPosition: lowestPosition,
                });
            }

            return underwater.sort((a, b) => (a.indicator < b.indicator) ? 1 : -1);
        },
        hasAssetsToBuy(state, getters) {
            return getters.assetsToBuy.length > 0;
        },
        hasAssetsToSell(state) {
            // counts, not the position list, which the API truncates to both price edges
            return Object.values(state.positions.actionableCounts).some(count => count > 0);
        },
    },
    mutations: {
        updateSyncTime(state, syncTook) {
            state.syncTook = syncTook.toFixed(2);
        },
        updateProcessingTime(state, processingTook) {
            state.processingTook = processingTook.toFixed(2);
        },
        updatePricingTime(state, pricingTime) {
            state.pricingTime = pricingTime + 'Z';
        },
        updateDbUsed(state, dbUsed) {
            state.dbUsed = dbUsed;
        },
        updateRequestWarnings(state, warnings) {
            state.requestWarnings = warnings || [];
        },
        storePrices(state, prices) {
            state.assetPrices = prices || {};
        },
        setPricingMode(state, mode) {
            // a cached response from an older build carries no mode; the sheet is the failsafe
            state.pricingMode = mode === PRICING_MODE.REALTIME
                ? PRICING_MODE.REALTIME
                : PRICING_MODE.DELAYED;
        },
        syncStarted(state) {
            state.syncStatus = SYNC_STATUS.SYNCING;
        },
        syncGetActionable(state) {
            state.syncStatus = SYNC_STATUS.GET_ACTIONABLE;
        },
        syncProcessing(state) {
            state.syncStatus = SYNC_STATUS.PROCESSING;
        },
        syncEnded(state) {
            state.syncStatus = SYNC_STATUS.SYNCED;
        },
        updateLastPositionsSync(state) {
            state.positions.lastSync = new Date;
        },
        setUserData(state, userData) {
            state.userData = userData;
        },
        setUserStats(state, userStats) {
            state.userStats = userStats;
        },
        pauseSync(state) {
            state.canSync = false;
        },
        resumeSync(state) {
            state.canSync = true;
        },
        cancelSyncTimer(state) {
            clearInterval(state.intervalCheckAPI);
            state.intervalCheckAPI = null;
        },
        updateLastEvents(state, events) {
            state.events = events;
        },
        setUpdateAvailable(state, registration) {
            state.updateAvailable = true;
            state.updateRegistration = registration;
        },
        dismissUpdate(state) {
            state.updateAvailable = false;
            state.updateRegistration = null;
        }
    },
    actions: {
        async addNewPositions({ commit, dispatch }, position) {
            const response = await apiPost('/api/add_position', { position });
            const positions = response.data;
            for (let i = 0; i < positions.length; i++) {
                commit('newlyAddedPosition', positions[i]);
            }

            // endpoint that has the last events list
            dispatch('retrieveLastOpenPositions');

            return response.data;
        },
        async reinvestPosition(_context, pid) {
            const response = await apiPost('/api/reinvest_position', { pid });
            return response.data;
        },
        async retrieveLastOpenPositions({ commit }) {
            const response = await apiGet('/api/positions/last_open');
            const positions = response.data.positions || [];
            const events = response.data.events || [];

            commit('updateLastEvents', events);

            positions.forEach((position) => {
                commit('newlyAddedPosition', position);
            });

            return response.data;
        },
        async retrieveToken({ commit, dispatch }, credentials) {
            const response = await apiPost('/api/login', {}, {
                auth: {
                    username: credentials.username,
                    password: credentials.password,
                },
            });
            const token = response.data.token;
            localStorage.setItem('access_token', token);
            commit('setAccessToken', token);

            // start from a clean sync state so a re-login can't inherit a stuck timer
            commit('resumeSync');
            commit('cancelSyncTimer');

            // update system
            dispatch('syncData');

            return response.data;
        },
        async destroyToken({ commit, getters }) {
            if (!getters.loggedIn) return;
            try {
                const response = await apiPost('/api/logout');
                return response.data;
            } finally {
                localStorage.removeItem('access_token');
                commit('destroyAccessToken');
                commit('resumeSync');
                commit('cancelSyncTimer');
            }
        },
        disableSyncTimer({ commit }) {
            commit('cancelSyncTimer');
        },
        async syncData({ commit, dispatch, state, getters }) {
            if (!state.canSync) {
                dispatch('refreshData');
                return;
            }

            commit('syncStarted');
            commit('updateRequestedSync');
            commit('pauseSync'); // 5 sec cooldown

            try {
                await dispatch('requestSync');
                commit('syncGetActionable');

                dispatch('refreshData');
                dispatch('retrieveLastOpenPositions');
            } catch (error) {
                // degrade to stale-this-cycle rather than dying until reload
                commit('updateRequestWarnings', ['Data sync failed — showing cached prices until the next refresh.']);
                console.error('syncData failed', error);
            } finally {
                setTimeout(() => {
                    commit('resumeSync');
                }, SYNC_COOLDOWN_MS);

                clearInterval(state.intervalCheckAPI);
                // don't re-arm the loop once logged out (session ended mid-sync)
                state.intervalCheckAPI = getters.loggedIn
                    ? setInterval(() => {
                        dispatch('syncData');
                    }, SYNC_REFRESH_INTERVAL_MS)
                    : null;
            }
        },
        async refreshData({ commit, dispatch }) {
            commit('syncProcessing');
            try {
                await dispatch('loadActionableData');
                dispatch('loadUserStats');
                commit('syncEnded');
            } catch (error) {
                commit('resumeSync');
                throw error;
            }
        },
        async requestSync({ commit }) {
            const response = await apiGet('/api/sync_data');
            commit('updateSyncTime', response.data.sync_took);
            commit('updatePricingTime', response.data.pricing_time);
            commit('updateDbUsed', response.data.db_used);
            commit('updateRequestWarnings', response.data.request_warnings);
            commit('storePrices', response.data.prices);
            return response.data;
        },
        async changePricingMode({ commit, dispatch }, mode) {
            /* The server owns the mode, so the toggle only moves once the write lands.
               A failed write leaves it where it was. */
            try {
                const response = await apiPost('/api/user/meta', { pricingMode: mode });
                commit('setPricingMode', response.data.pricing_mode);
            } catch (error) {
                commit('updateRequestWarnings', ['Could not change the pricing mode.']);
                console.error('changePricingMode failed', error);
                return;
            }

            // the stored price only moves on a sync, and the next one is 5 minutes out
            dispatch('syncData');
        },
        async loadActionableData({ commit, dispatch }) {
            commit('syncProcessing');
            const response = await apiGet('/api/positions/actionable');
            commit('updateProcessingTime', response.data.processing_took);

            const positions = response.data.positions;
            const assetList = response.data.assetList; // V2
            const userData = response.data.user;

            dispatch('updateAssets', assetList);

            // V1
            commit('updateLastPositionsSync');
            commit('setUserData', userData);
            commit('setPricingMode', response.data.preferences?.pricing_mode);

            // v2
            dispatch('ingestActionablePositions', {
                positions,
                counts: response.data.actionable_counts,
            });

            const currentTimestamp = new Date();
            console.log(currentTimestamp.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true }), 'refreshed actionable Positions');

            return response.data;
        },
        async loadAssetList({ commit, dispatch }) {
            try {
                // the only endpoint that carries disabled assets; actionable ships live ones
                const response = await apiGet('/api/assets');
                dispatch('updateAssets', response.data.assets);
            } catch (error) {
                commit('updateRequestWarnings', ['Failed to load the asset list.']);
                console.error('loadAssetList failed', error);
            }
        },
        async loadUserStats({ commit }) {
            const response = await apiGet('/api/user/stats');
            commit('setUserStats', response.data);
            return response.data;
        },
        showUpdatePrompt(context, registration) {
            context.commit('setUpdateAvailable', registration);
        },
        applyUpdate(context) {
            const registration = context.state.updateRegistration;
            if (!registration) return;
            // Reload is driven by the controllerchange listener in
            // registerServiceWorker.js once the new SW takes control.
            if (registration.waiting) {
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
            context.commit('dismissUpdate');
        },
        dismissUpdatePrompt(context) {
            context.commit('dismissUpdate');
        },
        checkForUpdates() {
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistration().then(reg => {
                    if (reg) {
                        reg.update();
                    }
                });
            }
        },
    },
});